/**
 * Access to each Garaimon's target colour profile.
 *
 * The data itself is machine generated into `colourProfiles.generated.ts` by
 * `scripts/colour_profiles.py`, either measured from sprite art or derived as a
 * labelled placeholder. This module is the only place the rest of the game reads
 * it, so there is one definition of what a profile is and one place that checks
 * the data is sound.
 *
 * A profile answers two questions, and both matter: which colours a creature
 * uses, and in what proportion. Gathering a creature's colours in the wrong
 * amounts is not a good match, which is the whole point of comparing shares
 * rather than checking which colours are present.
 */

import { COLLECT_CONFIG, RARITY_LADDER } from './collectConfig';
import { anchorIndexById, encodeColourIndex, mixtureFromShares, type ColourMixture } from './colour';
import { COLOUR_PROFILES_GENERATED, type GeneratedColourProfile } from './colourProfiles.generated';
import { creaturesOn, roster } from './roster';
import type { ContinentId } from './types';

export type ColourProfile = GeneratedColourProfile;

/** Every continent, in the roster's own order. */
const CONTINENTS: readonly ContinentId[] = ['africa', 'asia', 'europe', 'north-america', 'south-america', 'oceania'];

/** A six-digit hex colour as channels. Local so a bad string is reported here. */
function hexToRgb(value: string): [number, number, number] {
  const text = value.replace('#', '');
  if (!/^[0-9a-fA-F]{6}$/.test(text)) throw new Error(`Garaimon: "${value}" is not a six-digit hex colour.`);
  return [Number.parseInt(text.slice(0, 2), 16), Number.parseInt(text.slice(2, 4), 16), Number.parseInt(text.slice(4, 6), 16)];
}

export const COLOUR_PROFILES: readonly ColourProfile[] = COLOUR_PROFILES_GENERATED;

const PROFILE_BY_SPECIES = new Map(COLOUR_PROFILES.map((profile) => [profile.speciesId, profile] as const));

/**
 * A species' profile. A missing profile is a content bug rather than a normal
 * state, so it is reported with the species id instead of being substituted.
 */
export function profileFor(speciesId: string): ColourProfile {
  const profile = PROFILE_BY_SPECIES.get(speciesId);
  if (!profile) {
    throw new Error(
      `Garaimon: no colour profile exists for "${speciesId}". Run "python3 scripts/colour_profiles.py" to regenerate them; ` +
        'a species with no profile cannot be matched or hinted at.',
    );
  }
  return profile;
}

/** The profile as a mixture, ready to compare against a gathered collection. */
export function profileMixture(profile: ColourProfile): ColourMixture {
  return mixtureFromShares(profile.weights);
}

/** Every profile for a continent, in roster order, which is the order the picker offers. */
export function profilesOn(continent: ContinentId): ColourProfile[] {
  return creaturesOn(continent).map((creature) => profileFor(creature.id));
}

/**
 * Every profile on a continent as a mixture, ready to compare against.
 *
 * Building a mixture from its shares allocates, and the live composition readout
 * compares against every candidate on the continent several times a second. Caching
 * by continent means that comparison does no allocation in the render loop.
 *
 * The cache is keyed on the continent and never invalidated, because profiles are
 * fixed data compiled into the build, so there is nothing that could change them.
 */
const MIXTURE_CACHE = new Map<ContinentId, readonly { readonly profile: ColourProfile; readonly mixture: ColourMixture }[]>();

export function profileMixturesOn(continent: ContinentId): readonly { readonly profile: ColourProfile; readonly mixture: ColourMixture }[] {
  const cached = MIXTURE_CACHE.get(continent);
  if (cached) return cached;
  const built = profilesOn(continent).map((profile) => ({ profile, mixture: profileMixture(profile) }));
  MIXTURE_CACHE.set(continent, built);
  return built;
}

/** True when a profile was measured from sprite art rather than derived as a placeholder. */
export function isMeasured(profile: ColourProfile): boolean {
  return profile.source === 'sprite';
}

/**
 * Checks the profile data against the roster. Returns a list of problems, empty
 * when everything is consistent. Nothing is repaired silently: a problem here
 * means the generated file and the roster disagree, and that disagreement is the
 * bug to fix.
 */
export function colourProfileProblems(): string[] {
  const problems: string[] = [];
  for (const creature of roster) {
    const profile = PROFILE_BY_SPECIES.get(creature.id);
    if (!profile) {
      problems.push(`"${creature.id}" is in the roster but has no colour profile.`);
      continue;
    }
    const shares = profile.weights.reduce((sum, entry) => sum + entry.share, 0);
    if (Math.abs(shares - 1) > 1e-3) problems.push(`"${creature.id}" has shares summing to ${shares.toFixed(4)} instead of 1.`);
    if (profile.weights.length === 0) problems.push(`"${creature.id}" has a profile with no colours.`);
    if (profile.weights.some((entry) => entry.share <= 0)) problems.push(`"${creature.id}" has a colour with a non-positive share.`);
    if (profile.source === 'sprite' && !profile.measuredFrom) problems.push(`"${creature.id}" claims to be measured from art but says not which frame.`);
  }
  for (const profile of COLOUR_PROFILES) {
    if (!roster.some((creature) => creature.id === profile.speciesId)) problems.push(`"${profile.speciesId}" has a colour profile but is not in the roster.`);
  }
  return problems;
}

/**
 * The anchors one continent's creatures ask the board to carry.
 *
 * This is the union of every profile colour on the continent plus the shared
 * neutrals, which is exactly what `planForProfiles` demotes to the board's
 * palette. It is the number the availability budget is divided between.
 */
export function continentDemandedAnchors(continent: ContinentId): number[] {
  const demanded = new Set<number>();
  for (const creature of creaturesOn(continent)) {
    for (const weight of profileFor(creature.id).weights) demanded.add(anchorIndexById(weight.anchor));
  }
  for (const hex of COLLECT_CONFIG.neutralColours) demanded.add(encodeColourIndex(hexToRgb(hex)));
  return [...demanded].sort((a, b) => a - b);
}

/**
 * Checks that every continent's palette fits the availability budget.
 *
 * A board promises each demanded colour `anchorFloorShare` of itself, and the
 * promises together are capped at `availabilityBudget`. Past that cap the promises
 * are scaled down, so a continent that demands too many colours silently gets a
 * thinner guarantee for all of them - which is the failure this catches. It is not
 * a crash: it is a content budget, and the fix is to reuse anchors already
 * demanded rather than to add new ones.
 */
export function continentPaletteProblems(): string[] {
  const capacity = Math.floor(COLLECT_CONFIG.availabilityBudget / COLLECT_CONFIG.anchorFloorShare);
  const problems: string[] = [];
  for (const continent of CONTINENTS) {
    const demanded = continentDemandedAnchors(continent);
    if (demanded.length > capacity) {
      problems.push(
        `${continent} demands ${demanded.length} colours but the board can promise only ${capacity} ` +
          `(${(COLLECT_CONFIG.availabilityBudget * 100).toFixed(0)}% of a board divided into ${(COLLECT_CONFIG.anchorFloorShare * 100).toFixed(0)}% floors), ` +
          'so every promise on that continent would be scaled down. New creatures must reuse colours the continent already demands.',
      );
    }
  }
  return problems;
}

/**
 * Checks that the rarity ladder is a usable distribution.
 *
 * Only one thing can make it unusable: ceilings that together are less than the
 * whole encounter. Every tier's chance is capped at its ceiling, so if the caps do
 * not add up to 1 there is no way to fill the draw and some creature would have to
 * exceed its ceiling. Reported loudly rather than worked around, because the
 * ladder is the promise the game makes about how rare a rare creature is.
 */
export function rarityLadderProblems(): string[] {
  const finiteCeilings = RARITY_LADDER.map((rung) => rung.ceiling).filter((ceiling) => Number.isFinite(ceiling));
  const total = finiteCeilings.reduce((sum, ceiling) => sum + ceiling, 0);
  // A tier with no ceiling can always absorb the remainder, so the capped total
  // only has to reach 1 when every tier is capped.
  if (finiteCeilings.length === RARITY_LADDER.length && total < 1) {
    return [
      `the rarity ceilings sum to ${total.toFixed(3)}, less than the whole encounter, so no draw could fill one without exceeding a ceiling.`,
    ];
  }
  const problems: string[] = [];
  for (const rung of RARITY_LADDER) {
    if (!(rung.weight > 0)) problems.push(`the "${rung.tier}" rung has a weight of ${rung.weight}, so it could never be encountered.`);
    if (!(rung.ceiling > 0)) problems.push(`the "${rung.tier}" rung has a ceiling of ${rung.ceiling}, so it could never be encountered.`);
  }
  return problems;
}

/**
 * Reports profile problems to the console, loudly and once. Called before the
 * mini-game is first used, so a drifted generated file is visible rather than
 * showing up as odd-looking targeting.
 */
let reported = false;
export function reportColourProfileProblems(): void {
  if (reported) return;
  reported = true;
  const problems = [...colourProfileProblems(), ...continentPaletteProblems(), ...rarityLadderProblems()];
  if (problems.length === 0) return;
  console.error(
    `Garaimon: ${problems.length} problem(s) in the colour profiles, the continent palettes or the rarity ladder, so encounter matching will not behave as intended. ` +
      'Run "python3 scripts/colour_profiles.py" to regenerate the profiles, and check the roster against the palette budget.',
    problems,
  );
}

/** How many profiles are real measurements, for readouts and tests. */
export function measuredProfileCount(): number {
  return COLOUR_PROFILES.filter(isMeasured).length;
}
